import { useCallback, useMemo, useState } from "react";
import type { ProviderAllocationOverview } from "../../api/providerAllocation";
import {
  computeFigures,
  emptyEntry,
  isDraftChanged,
  type AllocationFigures,
  type Draft,
  type DraftEntry,
} from "./allocationDraft";

/** Everything the two allocation panels share about editing: the draft, what it is compared with, the derived figures and the submit rule. */
export interface AllocationForm {
  draft: Draft;
  /** The figures for the draft, or `null` until an overview has loaded. */
  figures: AllocationFigures | null;
  /** Whether the draft differs from what it started as. */
  changed: boolean;
  /** Whether the draft is complete, changed and within the allocable amount. */
  canSubmit: boolean;
  /** The last error to show under the form, cleared by the next edit. */
  error: string | null;
  setError: (message: string | null) => void;
  /** Starts a fresh draft, which is also the baseline `changed` is measured against. */
  reset: (initial: Draft) => void;
  /** Applies an edit to one provider's entry and clears any error. */
  setEntry: (vendorId: string, change: Partial<DraftEntry>) => void;
}

/** Holds the editable draft for an overview, and derives the figures and whether it may be submitted. */
export function useAllocationForm(
  overview: ProviderAllocationOverview | null,
): AllocationForm {
  const [draft, setDraft] = useState<Draft>({});
  const [baseline, setBaseline] = useState<Draft>({});
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback((initial: Draft): void => {
    setDraft(initial);
    setBaseline(initial);
  }, []);

  const setEntry = useCallback(
    (vendorId: string, change: Partial<DraftEntry>): void => {
      setError(null);
      setDraft((current) => ({
        ...current,
        [vendorId]: { ...(current[vendorId] ?? emptyEntry), ...change },
      }));
    },
    [],
  );

  const figures = useMemo(
    () => (overview ? computeFigures(overview, draft) : null),
    [overview, draft],
  );
  const changed =
    overview !== null && isDraftChanged(overview, draft, baseline);
  const canSubmit =
    figures !== null &&
    changed &&
    figures.remaining >= 0 &&
    !figures.hasInvalid;

  return {
    draft,
    figures,
    changed,
    canSubmit,
    error,
    setError,
    reset,
    setEntry,
  };
}
